import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinesses } from '../hooks/useBusinesses';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { Plus, Search, Mail, Phone, MapPin, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';

export default function Businesses() {
  const { businesses } = useBusinesses();
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');

  const ITEMS_PER_PAGE = 25;

  // Subscription filters
  const SUBSCRIPTION_FILTERS = [
    {
      value: 'all',
      label: 'All Subscriptions',
      count: businesses.length
    },
    {
      value: 'paid',
      label: 'Paid Plans',
      count: businesses.filter(b => b.subscriptionPlan === 'Starter' || b.subscriptionPlan === 'Steady' || b.subscriptionPlan === 'Growth').length
    },
    {
      value: 'starter',
      label: 'Starter',
      count: businesses.filter(b => b.subscriptionPlan === 'Starter').length
    },
    {
      value: 'steady',
      label: 'Steady',
      count: businesses.filter(b => b.subscriptionPlan === 'Steady').length
    },
    {
      value: 'growth',
      label: 'Growth',
      count: businesses.filter(b => b.subscriptionPlan === 'Growth').length
    },
    {
      value: 'free',
      label: 'Free',
      count: businesses.filter(b => !b.subscriptionPlan || b.subscriptionPlan === 'Free').length
    },
  ];

  const filteredBusinesses = businesses
    .filter(business => {
      // Search filter
      const matchesSearch = 
        (business.businessName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.ownerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Subscription filter
      let matchesSubscription = true;
      if (subscriptionFilter === 'paid') {
        matchesSubscription = business.subscriptionPlan === 'Starter' || business.subscriptionPlan === 'Steady' || business.subscriptionPlan === 'Growth';
      } else if (subscriptionFilter === 'starter') {
        matchesSubscription = business.subscriptionPlan === 'Starter';
      } else if (subscriptionFilter === 'steady') {
        matchesSubscription = business.subscriptionPlan === 'Steady';
      } else if (subscriptionFilter === 'growth') {
        matchesSubscription = business.subscriptionPlan === 'Growth';
      } else if (subscriptionFilter === 'free') {
        matchesSubscription = !business.subscriptionPlan || business.subscriptionPlan === 'Free';
      }
      
      return matchesSearch && matchesSubscription;
    });

  // Calculate pagination
  const totalItems = filteredBusinesses.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentBusinesses = filteredBusinesses.slice(startIndex, endIndex);

  // Reset to first page when search or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, subscriptionFilter]);

  const getStatusBadgeClasses = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'active') return 'Active';
    if (status === 'pending') return 'Pending';
    return 'Inactive';
  };

  return (
    <div className="space-y-4 md:space-y-6 px-4 md:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Service Providers</h1>
        {permissions.canEditEmployees && (
          // See #459 — org creation from within the admin app
          // <button
          //   onClick={() => navigate('/service-providers/create')}
          //   className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition-colors"
          //   disabled={true}
          // >
          //   <Plus className="h-4 w-4" />
          //   <span>Add Service Provider</span>
          // </button>
          <button
            onClick={() => navigate('https://dashboard.clerk.com/apps/app_36ufZ9AjrL4saA8ig1XzpMpIRvF/instances/ins_36xHy9WhqRimoMsfbg8gHQoUcNK/organizations')}
            className="bg-indigo-600 text-white px-4 py-3 md:py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-indigo-700 transition-colors active:bg-indigo-800 touch-manipulation">
            <Plus className="h-5 w-5 md:h-4 md:w-4" />
            <span className="font-medium">Add Service Provider</span>
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-3 md:py-2 border border-gray-300 rounded-lg text-base leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search by name, owner, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Subscription Filters */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Filter by Subscription:</p>
            <div className="flex flex-wrap gap-2">
              {SUBSCRIPTION_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setSubscriptionFilter(filter.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    subscriptionFilter === filter.value
                      ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Businesses Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentBusinesses.map((business) => {
                return (
                  <tr key={business.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/service-providers/${business.id}`)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">{business.businessName}</p>
                          <p className="text-sm text-gray-500">{business.ownerName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {business.email && (
                          <div className="flex items-center text-sm text-gray-900">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            <span className="truncate max-w-48">{business.email}</span>
                          </div>
                        )}
                        {business.phone && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            <span>{business.phone}</span>
                          </div>
                        )}
                        {business.address?.addressCity && business.address?.addressState && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                            <span>{business.address.addressCity}, {business.address.addressState}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Platform:</span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(business.status)}`}>
                              {getStatusLabel(business.status)}
                            </span>
                          </div>
                          {business.landingPageStatus && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 font-medium">Landing:</span>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(business.landingPageStatus)}`}>
                                {getStatusLabel(business.landingPageStatus)}
                              </span>
                            </div>
                          )}
                          {business.userId && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              Portal Access
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {business.subscriptionPlan || 'Free'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {business.createdAt ? new Date(business.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        }) : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {business.createdAt ? new Date(business.createdAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : ''}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls - Desktop */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} businesses
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
                    const showEllipsis = (page === 2 && currentPage > 4) || (page === totalPages - 1 && currentPage < totalPages - 3);
                    
                    if (!showPage && !showEllipsis) return null;
                    
                    if (showEllipsis) {
                      return (
                        <span key={`ellipsis-${page}`} className="px-2 py-1 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                          currentPage === page
                            ? 'bg-indigo-600 border-indigo-600 text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Businesses Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {currentBusinesses.map((business) => (
          <div
            key={business.id}
            onClick={() => navigate(`/service-providers/${business.id}`)}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:bg-gray-50 touch-manipulation cursor-pointer"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">{business.businessName}</p>
                  <p className="text-sm text-gray-500 truncate">{business.ownerName}</p>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(business.status)}`}>
                Platform: {getStatusLabel(business.status)}
              </span>
              {business.landingPageStatus && (
                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(business.landingPageStatus)}`}>
                  Landing: {getStatusLabel(business.landingPageStatus)}
                </span>
              )}
              {business.userId && (
                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  Portal Access
                </span>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-2 mb-3">
              {business.email && (
                <div className="flex items-center text-sm text-gray-900">
                  <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{business.email}</span>
                </div>
              )}
              {business.phone && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                  <span>{business.phone}</span>
                </div>
              )}
              {business.address?.addressCity && business.address?.addressState && (
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                  <span>{business.address.addressCity}, {business.address.addressState}</span>
                </div>
              )}
            </div>

            {/* Footer Info */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                <span className="font-medium">{business.subscriptionPlan || 'Free'}</span>
              </div>
              <div className="text-xs text-gray-500">
                {business.createdAt ? new Date(business.createdAt).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                }) : 'N/A'}
              </div>
            </div>
          </div>
        ))}

        {/* Pagination Controls - Mobile */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col space-y-3">
              <div className="text-sm text-gray-600 text-center">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </div>
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex-1 max-w-[140px] inline-flex items-center justify-center px-4 py-3 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg active:bg-gray-100 touch-manipulation"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex-1 max-w-[140px] inline-flex items-center justify-center px-4 py-3 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg active:bg-gray-100 touch-manipulation"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {filteredBusinesses.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No businesses found matching your search.</p>
        </div>
      )}
    </div>
  );
}
